import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ConfirmationService, MessageService } from "primeng/api";
import {Observable, Observer, Subject} from "rxjs";
import { map } from "rxjs/operators";
import { GlobalizationHttpService } from "../generated/globalization-http.service";
import {createRessourceFacade, replace} from "../generated/ressources";
import {
  ContentType,
  FinishingType,
  MaterialFamily,
  PackagingStatus,
  PartData,
  ProjectCategory,
  RecycabilityCalculation,
  RecycabilityClassification,
  Component as PackagingComponent,
  AdhesiveType,
  AdhesiveApplication,
  DensityModification,
  Material,
  Color,
  Component,
  Text
} from "../generated/types";
import { Guid } from "../types";
import { AuthService } from "./auth.service";

function getFilenameFromHeaders(headers: HttpHeaders) {
  const contentDisposition = headers.get('content-disposition');
  var result = contentDisposition.split(';')[1].trim().split('=')[1];
  return result.replace(/"/g, '');
}

export function URI(literals: TemplateStringsArray, ...values: (number | boolean | string | { name: string; items: any[] })[]) {
  let result = '';
  const length = Math.max(literals.length, values.length);
  for (let i = 0; i < length; i++) {
    result += literals[i];
    const value = values[i];
    if (value !== undefined) {
      if (typeof value === 'object') {
        result += value.items.map(v => value.name + '=' + encodeURIComponent(v)).join('&');
      } else {
        result += encodeURIComponent(values[i].toString());
      }
    }
  }
  return result;
}

type RessourceFacade = ReturnType<typeof createRessourceFacade>;

@Injectable({
  providedIn: 'root',
})
export class SharedService {

  constructor(private httpClient: HttpClient,
    private authService: AuthService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private globalizationHttpService: GlobalizationHttpService) {
  }

  private ressources: Record<string, string>;
  private _lang = new Subject<string>();

  res: RessourceFacade;

  public readonly lang : Observable<string> = this._lang;

  async loadRessources(lang: string) {
    this.ressources = await this.globalizationHttpService.get(lang).toPromise();
    this.res = createRessourceFacade(this.ressources);
    this._lang.next(lang);
  }

  newGuid(): Guid {

    const s4 = () => Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);

    return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
  }

  translate(text: Text | string) : string {

    if(typeof text === 'string') {
      return text;
    }

    if(text.kind === 'Value') {
      return text.value;
    }

    if(text.kind === 'Translation') {
      const value = this.ressources[text.key] ?? "#" + text.key + "#";
      if(text.args != null && Object.keys(text.args).length > 0) {
        const params : Record<string, string> = {};
        for(let key in text.args) {
          const value = text.args[key];
          if(value != null) {
            params[key] = this.translate(value);
          }
        }
        return replace(value, params);
      }

      return value;
    }

    let s = "";
    for(let item of text.items) {
      if(item != null) {
        s += this.translate(item);
      }
    }
    return s;
  }

  private getHeaders() {
    const authInfo = this.authService.authInfoBehavior.value;
    return authInfo
      ? new HttpHeaders({ 'Authorization': 'Bearer ' + authInfo.access_token })
      : new HttpHeaders();
  }

  get<T>(url: string, params?: { [param: string]: string | string[] }): Observable<T> {

    const headers = this.getHeaders();

    const options = params
      ? { headers, params: new HttpParams({ fromObject: params }) }
      : { headers };

    return this.httpClient.get<T>(url, options);
  }

  getBlob(url: string, params?: { [param: string]: string | string[] }) {

    const headers = this.getHeaders();

    const p = params
      ? new HttpParams({ fromObject: params })
      : undefined;

    return this.httpClient.get(url, { observe: 'response', headers, responseType: 'blob', params: p })
      .pipe(map(response => {
        return {
          blob: response.body,
          filename: getFilenameFromHeaders(response.headers)
        }
      }));
  }

  post<T>(url: string, body: any, params?: { [param: string]: string | string[] }): Observable<T> {

    const headers = this.getHeaders();

    const options = params
      ? { headers, params: new HttpParams({ fromObject: params }) }
      : { headers };

    return this.httpClient.post<T>(url, body, options);
  }

  put<T>(url: string, body: any, params?: { [param: string]: string | string[] }): Observable<T> {

    const headers = this.getHeaders();

    const options = params
      ? { headers, params: new HttpParams({ fromObject: params }) }
      : { headers };

    return this.httpClient.put<T>(url, body, options);
  }

  delete<T>(url: string, params?: { [param: string]: string | string[] }): Observable<T> {

    const headers = this.getHeaders();

    const options = params
      ? { headers, params: new HttpParams({ fromObject: params }) }
      : { headers };

    return this.httpClient.delete<T>(url, options);
  }


  notifySuccess(summary: string, detail: string) {
    this.messageService.add({ severity: 'success', summary, detail });
  }

  notifyError(summary: string, detail: string) {
    this.messageService.add({ severity: 'error', summary, detail });
  }

  logError(description: string) {
    let headers = this.getHeaders();
    headers = headers.set('Content-Type', 'application/json');
    return this.httpClient.post<number>('/api/log/error', JSON.stringify(description), { headers });
  }

  confirm(acceptLabel: string, rejectLabel: string, message: string): Promise<boolean> {
    return Observable.create((observer: Observer<boolean>) => {
      this.confirmationService.confirm({
        message,
        acceptLabel,
        rejectLabel,
        accept: () => {
          observer.next(true);
          observer.complete();
        },
        reject: () => {
          observer.next(false);
          observer.complete();
        }
      });
    }).toPromise();
  }

  getPartName(part: PartData) {
    return part.name && part.name.length > 0
      ? part.name
      : this.getComponentName(part.component);
  }

  getComponentName(componentType: PackagingComponent | null) {
    if (componentType == null) { return this.res['#Part']; }
    return (this.res.enums.Component as any)[Component[componentType]];
  }

  get booleanOptions() {
    return {
      ['true']: this.res.shared.yes,
      ['false']: this.res.shared.no,
    };
  }

  // get componentOptions() {
  //   const result : Record<string, string> = {};
  //   for(let key in Component) {
  //     const value = Number(key);
  //     if (isNaN(value)) { continue; }
  //     result[key] = (this.res.enums.Component as any)[Component[value]];
  //   }
  //   return result;
  // }

  get materialFamilyOptions() {
    return this.res.enums.MaterialFamily;
  }

  get materialOptions() {
    return this.res.enums.Material;
  }

  get colorOptions() {
    return this.res.enums.Color;
  }

  get densityModificationOptions() {
    return {
      ...this.res.enums.DensityModification,
      [DensityModification.None]: ''
    };
  }

  get recycabilityClassificationOptions() {
    return {
      ...this.res.enums.RecycabilityClassification,
      [RecycabilityClassification.Unknown]: '?'
    };
  }

  get projectCategoryOptions() {
    return {
      [ProjectCategory.Standard]: this.res['#Standard'],
      [ProjectCategory.Light]: this.res['#Light'],
      [ProjectCategory.Silver]: this.res['#Silver'],
      [ProjectCategory.Gold]: this.res['#Gold'],
      [ProjectCategory.Combination]: this.res['#Combination'],
    };
  }

  get packagingStatusOptions() {
    return {
      [PackagingStatus.Prototype]: this.res['#Prototype'],
      [PackagingStatus.Model]: this.res['#Model'],
      [PackagingStatus.Circulation]: this.res['#Circulation']
    };
  }

  get contentTypeOptions() {
    return {
      ...this.res.enums.ContentType,
      [ContentType.None]: ''
    };
  }

  get recycabilityCalculationOptions() {
    return this.res.enums.RecycabilityCalculation;
  }

  get finishingTypeOptions() {
    return {
      ...this.res.enums.FinishingType,
      [FinishingType.None]: ''
    };
  }

  get adhesiveTypeOptions() {

    return {
      ...this.res.enums.AdhesiveType,
      [AdhesiveType.None]: '',
    };
  }

  get adhesiveApplicationOptions() {
    return this.res.enums.AdhesiveApplication;
  }
}
